
import { Vue, Component, Prop, Watch } from 'vue-property-decorator';
import { namespace } from 'vuex-class';
import * as PleasedApi from '@/common/api/api.pleased';
import { SearchArticle } from '@/common/api/models.pleased';
import waiting from '@/components/loading/waiting';
import { emitOpenArticle, onArticleAutocompleteFocused, onArticleAutocompleteCollapse } from '@/common/eventBus';
import { debounce, markSearchTextToHtml } from '@/vendor/pleased-sdk/util';
import ArticleListItem from './ArticleListItem.vue';
import AttachmentIcon from './icons/AttachmentIcon.vue';
import SearchIcon from './icons/SearchIcon.vue';
import HeadArrowUpIcon from './icons/HeadArrowUpIcon.vue';
import ClearIcon from './icons/ClearIcon.vue';

import * as Locale from '@/plugins/i18n';
import { LETTERS_DIGITS_SPACES_REGEXP, MAX_ARTICLE_AUTOCOMPLETE_CHAR_NUM, MIN_ARTICLE_AUTOCOMPLETE_TEXT_LENGTH } from '@/common/config';
import { PleasedUserInfo } from '@/common/types';
import { reportingService } from '@/common/reporting';
import trackClick, { BTN_EVTS } from '@/directives/trackClick';
import { analytics } from '@/common/logs/EventLogger';

const userModule = namespace('user');
const articleAutocompleteModule = namespace('articleAutocomplete');

@Component({
  components: { ArticleListItem, AttachmentIcon, SearchIcon, HeadArrowUpIcon, ClearIcon },
  directives: {
    waiting,
    trackClick,
  },
})
export default class ArticleAutocomplete extends Vue {
  @userModule.State('pleasedUser') pleasedUser!: PleasedUserInfo;

  @articleAutocompleteModule.State('searchHistory') searchHistory!: { [key: string]: SearchArticle[] };
  @articleAutocompleteModule.Mutation addSearchHistoryItem!: (payload: { searchHistoryItem: SearchArticle, userId?: number | string }) => void;

  @Prop({ default: null }) readonly title!: string | null;
  @Prop(Boolean) readonly defaultCollapsed!: boolean;
  @Prop({ type: String, default: '' }) readonly trackEventContext!: string;
  @Prop(Number) readonly trackEventSelection!: number;

  readonly MAX_ARTICLE_AUTOCOMPLETE_CHAR_NUM = MAX_ARTICLE_AUTOCOMPLETE_CHAR_NUM;
  readonly MIN_ARTICLE_AUTOCOMPLETE_TEXT_LENGTH = MIN_ARTICLE_AUTOCOMPLETE_TEXT_LENGTH;
  readonly BTN_EVTS = BTN_EVTS;

  $refs!: { inputRef: HTMLInputElement }

  public isCollapsed: boolean = false;
  public query: string = '';
  public showDropdown: boolean = false;
  public loading: boolean = false;
  public searchData: SearchArticle[] = [];

  cleanUps: Function[] = [];

  debouncedFetchSearchData = debounce(this.fetchArticles, 500);

  get isEmpty (): boolean {
    return this.dropdownData.length === 0;
  }

  get dropdownData (): SearchArticle[] {
    const currentLang = Locale.i18n.locale as Locale.SupportedLanguages;
    const key = `${this.pleasedUser.userId}-${currentLang}`;

    const searchedData = this.searchHistory[key] || [];
    return this.validQueryText ? this.searchData : searchedData;
  }

  get validQueryText () {
    const searchText = this.query?.trim();

    return searchText && searchText.length >= MIN_ARTICLE_AUTOCOMPLETE_TEXT_LENGTH;
  }

  getArticleItemTrackParams (article: SearchArticle) {
    const eventName = this.validQueryText
      ? BTN_EVTS.SEARCH_ARTICLE_RESULT
      : BTN_EVTS.SEARCH_ARTICLE_RECENT_SEARCH_ARTICLE;

    return {
      name: `${this.trackEventContext}${eventName}`,
      selection: article.id,
    };
  }

  created () {
    this.isCollapsed = this.defaultCollapsed;

    this.cleanUps.push(onArticleAutocompleteFocused((isFocused) => {
      if (isFocused) {
        this.$refs.inputRef.focus();
      }
    }), onArticleAutocompleteCollapse((isCollapsed) => {
      this.isCollapsed = isCollapsed;
    }));
  }

  async fetchArticles (): Promise<void> {
    const searchText = this.query?.trim();
    if (!searchText) return;
    const articles = await PleasedApi.getArticleSearch.cached({
      searchText,
    }).finally(() => {
      this.loading = false;
    });

    this.searchData = articles.data;
  }

  beforeDestroy () {
    this.cleanUps?.forEach(c => { try { c?.(); } finally {} });
  }

  setSearchTextFromEvent (e: Event): void {
    const target = e.target as HTMLInputElement;

    let value = target.value;

    value = value.replace(LETTERS_DIGITS_SPACES_REGEXP, '');

    if (value.length > MAX_ARTICLE_AUTOCOMPLETE_CHAR_NUM) {
      value = value.substring(0, MAX_ARTICLE_AUTOCOMPLETE_CHAR_NUM);
    }

    this.query = value;

    const trimmedValue = value?.trim();

    if (trimmedValue && trimmedValue.length >= MIN_ARTICLE_AUTOCOMPLETE_TEXT_LENGTH) {
      this.loading = true;
      this.debouncedFetchSearchData();
    } else {
      this.loading = false;
    }
  }

  openArticle (article: SearchArticle): void {
    reportingService.selectedArticleFromSearch(article.id);
    analytics.trackClick(this.getArticleItemTrackParams(article));
    PleasedApi.saveArticleHit({ articleId: article.id, searchText: this.query });

    this.addSearchHistoryItem({ searchHistoryItem: article, userId: this.pleasedUser.userId });

    emitOpenArticle({
      isOpen: true,
      articleId: article.id,
      fetchArticle: true,
      isFromAutocomplete: true,
      categoryName: article.categoryName,
    });
  }

  changeShowingDropdown (value: boolean) {
    this.showDropdown = value;
  }

  clearInput () {
    this.query = '';
  }

  highlightText (title: string) {
    return markSearchTextToHtml(title, this.query.trim());
  }

  toggleCollapsed () {
    this.isCollapsed = !this.isCollapsed;

    if (this.isCollapsed) {
      this.showDropdown = false;
    } else {
      this.$refs.inputRef.focus();
    }
  }

  @Watch('defaultCollapsed')
  onDefaultCollapsedChange (val: boolean) {
    this.isCollapsed = val;
  }
}
